import React, { useRef, useState, useEffect } from "react";
import Scaffold from "../../widget/layouts/Scaffold";
import AppTranslation from "../app_translation/AppTranslation";
import { AlertMesage } from "../../widget/common/ui/Alerts";
import { textCase } from "../ui/UI";
import { ResponsiveDeviceType } from "../../widget/layouts/ScreenMedia";
import AppTheme from "../app_config/AppTheme";
import { useDispatch } from "react-redux";
import { provideFileString } from "../../libraries/state_reducers/service/ImageEditorReducer";
import {
  addModal,
  removeModal,
} from "../../libraries/state_reducers/widget/ModalsReducer";
import ModalKeys from "../../const/ModalKeys";
import { Col } from "react-bootstrap";
import { IconCircleFilledIcon } from "../../widget/common/ui/Icon";
import { ImageEditorPick } from "../image_editor/ImageEditor";
import { isMobile } from "react-device-detect";
import { Position } from "../../widget/common/ui/Stack";
import UtilityHelper from "../utilities/UtilityHelper";
import { TimerMinutesSeconds } from "../../widget/common/ui/Timer";

interface ZoomConstraint {
  zoom?: number;
}

const CameraCaptureScreen = (props: { screenMobileView: boolean }) => {
  const type: number = ResponsiveDeviceType();
  const dispatch = useDispatch();

  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const trackRef = useRef<MediaStreamTrack | null>(null);
  const streamVideoRef = useRef<MediaStream | null>(null);
  const cameraTypeRef = useRef<"photo" | "video">("photo");
  const isFrontCameraRef = useRef<boolean | null>(false);

  const durationSecondsRef = useRef<number>(0);

  const [cameraType, setCameraType] = useState<"photo" | "video">("photo");
  const [access, setAccess] = useState<boolean | null>(false);
  const [zoom, setZoom] = useState<number>(1);
  //const [zoomRange, setZoomRange] = useState({ min: 1, max: 3 });
  const [torchEnabled, setTorchEnabled] = useState<boolean>(false);

  const [recording, setRecording] = useState<boolean>(false);
  const [recordedChunks, setRecordedChunks] = useState<Blob[]>([]);

  // Start camera
  const startCamera = async (isFront: boolean) => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        //video: { facingMode: "environment" }, // rear camera by default
        video: { facingMode: { ideal: isFront ? "user" : "environment" } }, // USE BACK AND FRONT IF AVAILABLE
        //video: { facingMode: { exact: "environment" } }, //BACK
        //video: { facingMode: { exact: "user" } }, // FRONT
        audio: cameraTypeRef.current === "video" ? true : false,
      });

      const track = stream.getVideoTracks()[0];
      trackRef.current = track;

      streamVideoRef.current = stream;

      // Read supported zoom constraints (if supported)
      const capabilities = track.getCapabilities?.() as any;
      if (capabilities?.zoom) {
        /* setZoomRange({
          min: capabilities.zoom.min,
          max: capabilities.zoom.max,
        }); */
        setZoom(capabilities.zoom.min);
      }

      setAccess(true);

      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
      }
    } catch (err) {
      console.error("Error accessing camera:", err);
      AlertMesage({
        title: textCase("Camera access denied or error"),
      });
      setAccess(null);
    }
  };

  // Take picture
  const takePicture = () => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas) return;

    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

    // Convert canvas to image URL
    const imageData = canvas.toDataURL("image/png");

    if (imageData) {
      dispatch(removeModal(ModalKeys.chat_camera));
      if (type !== 3) {
        window.history.replaceState(null, "", window.location.pathname);
      }

      dispatch(
        provideFileString({
          fileString: imageData,
          fromAction: "chat",
        })
      );
      dispatch(
        addModal({
          id: ModalKeys.image_editor,
          size: "lg",
          screenMobileView: type === 3 ? false : true,
        })
      );
    }
  };

  const handleZoom = async (value: number) => {
    setZoom(value);
    try {
      const constraints: MediaTrackConstraints & {
        advanced?: ZoomConstraint[];
      } = {
        advanced: [{ zoom: value }],
      };

      await trackRef.current?.applyConstraints(constraints);
    } catch (e) {
      console.error("Zoom not supported", e);
    }
  };

  const closeCamera = () => {
    dispatch(removeModal(ModalKeys.chat_camera));
    if (type !== 3) {
      window.history.replaceState(null, "", window.location.pathname);
    }
  };

  const switchCamera = () => {
    isFrontCameraRef.current = !isFrontCameraRef.current;
    stopCamera();

    startCamera(isFrontCameraRef.current!);
  };

  const toggleZoom = () => {
    let currentZoom: number = zoom + 0.5;
    if (currentZoom > 5) {
      currentZoom = 1;
    }

    handleZoom(currentZoom);
    //setZoom(currentZoom);
  };

  const toggleFlash = async () => {
    if (!trackRef) return;

    const capabilities = trackRef.current!.getCapabilities() as any;

    // Check if torch is supported
    if (!capabilities.torch) {
      //alert("Flash (torch) is not supported on this device/browser.");
      AlertMesage({
        title: "Flash (torch) is not supported on this device/browser.",
        onClick: () => {},
      });
      return;
    }

    try {
      await trackRef.current!.applyConstraints({
        advanced: [{ torch: !torchEnabled }] as any,
      });
      setTorchEnabled(!torchEnabled);
    } catch (error) {
      console.error("Torch error:", error);
    }
  };

  const swicthCameraType = (camType: "photo" | "video" = "photo") => {
    setCameraType(camType);
    cameraTypeRef.current = camType;

    stopCamera();
    setRecording(false);
    durationSecondsRef.current = 0;

    startCamera(isFrontCameraRef.current!);
  };

  // Start recording
  const startRecording = () => {
    console.log("startRecording", streamVideoRef.current);
    //setRecording(true);
    //return false;
    if (!streamVideoRef.current) return;

    // Pick a supported MIME type
    const mimeTypes = [
      "video/webm;codecs=vp9",
      "video/webm;codecs=vp8",
      "video/webm",
      "video/mp4", // iOS only under certain conditions
    ];

    let mimeType = "";
    for (const type of mimeTypes) {
      if (MediaRecorder.isTypeSupported(type)) {
        mimeType = type;
        break;
      }
    }

    console.log("Using MIME type:", mimeType);

    const mediaRecorder = new MediaRecorder(
      streamVideoRef.current!,
      mimeType ? { mimeType } : {}
    );
    mediaRecorderRef.current = mediaRecorder;
    let chunks: Blob[] = [];
    //setRecordedChunks([]);

    mediaRecorder.ondataavailable = (event) => {
      if (event.data.size > 0) {
        chunks.push(event.data);
        //setRecordedChunks((prev) => [...prev, event.data]);
      }
    };

    /* console.log(chunks);
    mediaRecorder.onstop = () => {
      // const blob = new Blob(chunks, { type: mimeType || "video/webm" });
      // const url = URL.createObjectURL(blob);
      // console.log("Recording URL:", url);

      console.log(chunks);
    }; */

    setRecordedChunks(chunks);
    mediaRecorder.start();
    setRecording(true);
  };

  // Stop recording
  const stopRecording = async () => {
    console.log(`stopRecording: ${recording}`);
    //setRecording(false);
    if (mediaRecorderRef.current && recording) {
      mediaRecorderRef.current.stop();
      setRecording(false);

      await UtilityHelper.delay(100);
      downloadVideo();
    }
  };

  // Download video
  const downloadVideo = () => {
    console.log("downloadVideo", recordedChunks.length);
    if (recordedChunks.length === 0) return;
    const blob = new Blob(recordedChunks, { type: "video/webm" });
    console.log(blob);

    dispatch(removeModal(ModalKeys.chat_camera));
    if (type !== 3) {
      window.history.replaceState(null, "", window.location.pathname);
    }

    dispatch(
      provideFileString({
        fileString: URL.createObjectURL(blob),
        fromAction: "chat",
        durationSeconds: durationSecondsRef.current,
      })
    );
    dispatch(
      addModal({
        id: ModalKeys.video_editor,
        size: "lg",
        screenMobileView: type === 3 ? false : true,
      })
    );

    /* const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "recorded-video.webm";
    a.click();
    URL.revokeObjectURL(url); */
  };

  const stopCamera = () => {
    if (streamVideoRef.current) {
      streamVideoRef.current.getTracks().forEach((track) => track.stop());
      streamVideoRef.current = null;
    }

    /*  if (mediaRecorderRef.current) {
       mediaRecorderRef.current.stop();
     } */

    if (trackRef.current) {
      trackRef.current.stop();
      trackRef.current = null;
    }

    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
  };

  const videoTimer = (seconds: number) => {
    console.log(seconds);

    if (seconds === UtilityHelper.defaultAudioMaxSeconds) {
      durationSecondsRef.current = seconds;
      stopRecording();
    }
  };

  useEffect(() => {
    startCamera(isFrontCameraRef.current!);

    return () => {
      stopCamera();
    };
  }, []);

  const bodyContent: JSX.Element = (
    <div
      className="text-center h-100 bgcolor-black position-relative"
      style={{ display: "grid", gridTemplateRows: "1fr 45px" }}
    >
      {/* Camera preview */}
      <div className="position-relative">
        {cameraType === "video" && (
          <Position top={0} left={0} right={0} className="pt-2">
            <TimerMinutesSeconds
              startStatus={recording}
              callback={videoTimer}
              className="font-gotham-bold color-primary"
            />
          </Position>
        )}

        <video
          ref={videoRef}
          autoPlay
          playsInline
          muted
          className="h-100 w-100"
          style={{
            objectFit: "cover",
            //transform: `scale(${zoom})`,
            //transition: "transform 0.2s ease",
          }}
        />

        <Position bottom={0} left={0} right={0} className="z-index-1">
          <div className="row mx-0 py-4">
            {access && (
              <>
                <Col xs="3" className="d-center">
                  {cameraType === "photo" && (
                    <ImageEditorPick
                      fromAction="chat"
                      callback={() => {}}
                      onClick={() => closeCamera()}
                    >
                      <IconCircleFilledIcon
                        size={40}
                        name="gallery"
                        color="white"
                        bgColor="bgcolor-white"
                      />
                    </ImageEditorPick>
                  )}
                </Col>
                <Col className="d-flex align-items-center justify-content-between">
                  <div style={{ width: 30 }}></div>
                  <button
                    onClick={
                      cameraType === "photo"
                        ? takePicture
                        : recording
                        ? stopRecording
                        : startRecording
                    }
                    style={{
                      borderRadius: " 50%",
                      width: 50,
                      height: 50,
                      display: "inline-block",
                      outline: ` 3px solid ${AppTheme.white}`,
                      backgroundColor:
                        cameraType === "photo"
                          ? AppTheme.white
                          : recording
                          ? AppTheme.primary
                          : AppTheme.white,
                      outlineOffset: 3,
                      border: 0,
                    }}
                  />
                  <div style={{ width: 30 }}>
                    <div
                      className="camera-zoom-toggle font-gotham-book font-size-10_12"
                      role="button"
                      onClick={() => toggleZoom()}
                    >
                      {zoom.toFixed(1)}x
                    </div>
                  </div>
                </Col>
                <Col xs="3" className="d-center">
                  <IconCircleFilledIcon
                    size={40}
                    name="swicth2"
                    color="black"
                    bgColor="bgcolor-white"
                    handleClick={() => switchCamera()}
                  />
                </Col>
              </>
            )}
          </div>
        </Position>
      </div>

      {isMobile && access && (
        <div className="camera-zoom w-100 p-5 h-100">
          {/* ZOOM SLIDER */}
          <input
            type="range"
            min={1}
            max={5}
            step={0.5}
            value={zoom}
            //onChange={(e) => setZoom(parseFloat(e.target.value))}
            onChange={(e) => handleZoom(Number(e.target.value))}
          />
        </div>
      )}

      <div
        className={`position-relative d-center bgcolor-${
          access ? "white" : "black"
        } color-black text-uppercase font-gotham-book font-size-14_15`}
      >
        {access && (
          <>
            <div
              className={`px-3 py-2 ${
                cameraType === "video" ? "font-gotham-bold color-primary" : ""
              }`}
              role="button"
              onClick={() => swicthCameraType("video")}
            >
              {AppTranslation.text("video")}
            </div>
            <div
              className={`px-3 py-2 ${
                cameraType === "photo" ? "font-gotham-bold color-primary" : ""
              }`}
              role="button"
              onClick={() => swicthCameraType("photo")}
            >
              {AppTranslation.text("photo")}
            </div>
          </>
        )}
      </div>

      <canvas ref={canvasRef} style={{ display: "none" }} />
    </div>
  );

  return (
    <>
      {props.screenMobileView === true ? (
        <Scaffold
          appBarTitle={AppTranslation.text("camera")}
          appBarIcon="close"
          appBarAction={
            isMobile &&
            access && (
              <div role="button" onClick={toggleFlash}>
                {!torchEnabled ? (
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="18"
                    height="18"
                    fill="currentColor"
                    className="bi bi-lightning"
                    viewBox="0 0 16 16"
                  >
                    <path d="M5.52.359A.5.5 0 0 1 6 0h4a.5.5 0 0 1 .474.658L8.694 6H12.5a.5.5 0 0 1 .395.807l-7 9a.5.5 0 0 1-.873-.454L6.823 9.5H3.5a.5.5 0 0 1-.48-.641zM6.374 1 4.168 8.5H7.5a.5.5 0 0 1 .478.647L6.78 13.04 11.478 7H8a.5.5 0 0 1-.474-.658L9.306 1z" />
                  </svg>
                ) : (
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="18"
                    height="18"
                    fill="currentColor"
                    className="bi bi-lightning-fill"
                    viewBox="0 0 16 16"
                  >
                    <path d="M5.52.359A.5.5 0 0 1 6 0h4a.5.5 0 0 1 .474.658L8.694 6H12.5a.5.5 0 0 1 .395.807l-7 9a.5.5 0 0 1-.873-.454L6.823 9.5H3.5a.5.5 0 0 1-.48-.641z" />
                  </svg>
                )}
              </div>
            )
          }
          isModal={true}
        >
          {bodyContent}
        </Scaffold>
      ) : (
        bodyContent
      )}
    </>
  );
};

export default CameraCaptureScreen;
export const routeName: string = "/camera-capture";
